/**
 * Store List — React Query hook for multi-storefront registry
 *
 * Calls control_plane.api.storefronts.list_storefronts via raw fetch()
 * (NOT frappeMethod — that routes to Tenant Server, not Control Plane).
 * Returns the list of storefronts for the current tenant.
 *
 * Pattern: Matches useGetTenantUsage.ts — raw fetch with graceful empty fallback.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getCPToken, CONTROL_PLANE_URL } from "@/lib/api/controlPlaneClient";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────

export interface StorefrontEntry {
  storefront_key: string;
  label: string;
  domain: string;
  subdomain: string;
  is_primary: boolean | number;
  color_index: number;
  enabled: boolean | number;
}

export interface SubdomainCheckResult {
  available: boolean;
  reason: string | null;
}

// ── Color palette for store dots (8 distinguishable colors) ──

export const STORE_COLORS = [
  "bg-sky-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-orange-500",
  "bg-teal-500",
  "bg-fuchsia-500",
] as const;

export function getStoreColor(colorIndex: number): string {
  return STORE_COLORS[colorIndex % STORE_COLORS.length];
}

/**
 * Generate 2-letter abbreviation from a store label.
 * First letters of first two words, or first two letters of single word.
 */
export function getStoreAbbreviation(label: string): string {
  const words = label.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}

// ── Fetcher ──────────────────────────────────────────────

async function fetchStoreList(): Promise<StorefrontEntry[]> {
  const token = getCPToken();
  // Not authenticated yet — empty is the correct answer, not an error.
  if (!token) return [];

  // A transient failure must THROW (not return []). Returning [] here made
  // React Query cache "no stores" as a success for staleTime, silently hiding
  // ALL multi-store UI (store filter tabs, per-order store badges) for minutes.
  // Throwing lets React Query retry and keep the previous data via placeholderData.
  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.storefronts.list_storefronts`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({ access_token: token }),
    }
  );

  if (!response.ok) {
    throw new Error(`list_storefronts failed: ${response.status}`);
  }

  const data = await response.json();
  return (data.message?.storefronts as StorefrontEntry[]) ?? [];
}

// ── Hook ─────────────────────────────────────────────────

export function useStoreList(
  options: { enabled?: boolean } = {},
): UseQueryResult<StorefrontEntry[], Error> {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const currentTenant = useAuthStore((s) => s.currentTenant);

  return useQuery({
    queryKey: ["store-list", currentTenant],
    queryFn: fetchStoreList,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    enabled: (options.enabled ?? true) && hasHydrated && isAuthenticated && !!currentTenant,
    placeholderData: (prev) => prev,
  });
}

// ── Subdomain check ──────────────────────────────────────

export async function checkSubdomain(subdomain: string): Promise<SubdomainCheckResult> {
  try {
    const token = getCPToken();
    if (!token) return { available: false, reason: "Not authenticated" };

    const response = await fetch(
      `${CONTROL_PLANE_URL}/api/method/control_plane.api.storefronts.check_subdomain`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ access_token: token, subdomain }),
      }
    );

    if (!response.ok) return { available: false, reason: "Check failed" };

    const data = await response.json();
    return (data.message as SubdomainCheckResult) ?? { available: false, reason: "Unknown" };
  } catch {
    return { available: false, reason: "Network error" };
  }
}

// ── Create storefront mutation ───────────────────────────

interface CreateStorefrontParams {
  label: string;
  subdomain: string;
  price_list?: string;
}

interface CreateStorefrontResult {
  storefront?: StorefrontEntry;
  error?: string;
  max?: number;
}

async function createStorefront(params: CreateStorefrontParams): Promise<CreateStorefrontResult> {
  const token = getCPToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.storefronts.create_storefront`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        label: params.label,
        subdomain: params.subdomain,
        price_list: params.price_list || "Standard Selling",
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const serverMsg = errorData?._server_messages;
    if (serverMsg) {
      try {
        const parsed = JSON.parse(JSON.parse(serverMsg)[0]);
        throw new Error(parsed.message || "Failed to create store");
      } catch (e) {
        if (e instanceof Error && e.message !== "Failed to create store") throw e;
      }
    }
    throw new Error("Failed to create store");
  }

  const data = await response.json();
  return (data.message as CreateStorefrontResult) ?? {};
}

export function useCreateStorefront() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createStorefront,
    onSuccess: (data) => {
      if (data.error === "plan_limit") {
        // Plan limit — handled by caller
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["store-list"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-usage"] });
      if (data.storefront) {
        toast.success(`${data.storefront.label} created`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Could not create store. Please try again.");
    },
  });
}

// ── Update storefront mutation ───────────────────────────

interface UpdateStorefrontParams {
  storefront_key: string;
  label?: string;
  subdomain?: string;
}

interface UpdateStorefrontResult {
  storefront?: StorefrontEntry;
}

async function updateStorefront(params: UpdateStorefrontParams): Promise<UpdateStorefrontResult> {
  const token = getCPToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.storefronts.update_storefront`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({ access_token: token, ...params }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const serverMsg = errorData?._server_messages;
    if (serverMsg) {
      try {
        const parsed = JSON.parse(JSON.parse(serverMsg)[0]);
        throw new Error(parsed.message || "Failed to update store");
      } catch (e) {
        if (e instanceof Error && e.message !== "Failed to update store") throw e;
      }
    }
    throw new Error("Failed to update store");
  }

  const data = await response.json();
  return (data.message as UpdateStorefrontResult) ?? {};
}

export function useUpdateStorefront() {
  const queryClient = useQueryClient();
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey);
  const setActiveStorefront = useAuthStore((s) => s.setActiveStorefront);

  return useMutation({
    mutationFn: updateStorefront,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["store-list"] });
      // A subdomain change renames the storefront_key everywhere — follow it
      // in the active-store selection, or scoped pages break on a dead key
      if (
        data.storefront &&
        activeStorefrontKey === variables.storefront_key &&
        data.storefront.storefront_key !== variables.storefront_key
      ) {
        setActiveStorefront(data.storefront.storefront_key);
      }
      if (data.storefront) {
        toast.success(`${data.storefront.label} updated`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Could not update store. Please try again.");
    },
  });
}
